import { CalendarType, TenantType } from '@xeetr/types/enums'
import { CreateDailyNoteDto, UpdateDailyNoteDto } from '@xeetr/types/models'
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  format,
  getWeek,
  isSameDay,
  startOfMonth,
} from 'date-fns'
import { de } from 'date-fns/locale'
import {
  ChevronLeft,
  ChevronRight,
  CogIcon,
  FileText,
  Maximize,
  Minimize,
  NotebookPen,
  RefreshCw,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useUserSettings } from '@/hooks/useUserSettings'
import { useUpdateCalendarPreferences } from '@/hooks/requests/useUpdateCalendarPreferences'

import { DailyNoteModal } from './DailyNoteModal'

import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCalendar } from '@/hooks/useCalendar'
import { useCalendarUI } from '@/hooks/useCalendarUI'
import { useDailyNotes } from '@/hooks/useDailyNotes'
import { useTenant } from '@/hooks/useTenant'
import { cn } from '@/lib/utils'
import { Switch } from '../ui/switch'

interface Props {
  changeDate: (date: Date | Date[] | string) => void
}

const Toolbar = ({ changeDate }: Props) => {
  const { fullscreen, setFullscreen, showNoteView, setShowNoteView } = useCalendarUI()

  const { date: selectedDate, calendarType, setCalendarType, setDate } = useCalendar()
  const { tenant, updateTenantSettingsBatch } = useTenant()

  const [showNoteModal, setShowNoteModal] = useState(false)
  const [isTimeRangePopoverOpen, setIsTimeRangePopoverOpen] = useState(false)
  const [minTime, setMinTime] = useState(tenant?.calendarTimeRange?.minTime || '00:00')
  const [maxTime, setMaxTime] = useState(tenant?.calendarTimeRange?.maxTime || '23:59')

  // interval preference is stored per-user
  const { settings: userSettings, getCalendarPreferences } = useUserSettings()
  const { updateCalendarPreferences } = useUpdateCalendarPreferences()
  const tenantId = tenant?.id
  const initialInterval = getCalendarPreferences()?.timeInterval ?? 30
  const [timeInterval, setTimeInterval] = useState<number>(initialInterval)
  const [showUtilization, setShowUtilization] = useState<boolean>(
    getCalendarPreferences()?.showUtilization ?? false
  )
  // new slot duration preference: how long each calendar slot should be
  const initialSlotDuration = getCalendarPreferences()?.slotDuration ?? 15
  const [slotDuration, setSlotDuration] = useState<number>(initialSlotDuration)

  useEffect(() => {
    if (tenant?.calendarTimeRange) {
      setMinTime(tenant.calendarTimeRange.minTime)
      setMaxTime(tenant.calendarTimeRange.maxTime)
    }
  }, [tenant?.calendarTimeRange])

  // reset interval (and slot duration) when tenant or settings change
  useEffect(() => {
    const val = getCalendarPreferences()?.timeInterval ?? 30
    setTimeInterval(val)
    const slot = getCalendarPreferences()?.slotDuration ?? 15
    setSlotDuration(slot)
  }, [tenantId, userSettings.calendar])

  const handleSave = async () => {
    if (!tenant) return

    // Validate that minTime is before maxTime
    if (minTime >= maxTime) {
      // Could add toast notification here
      return
    }

    // update tenant-wide range
    await updateTenantSettingsBatch({
      calendarTimeRange: {
        minTime,
        maxTime,
      },
    })
    // also persist user preference for interval
    if (tenantId) {
      updateCalendarPreferences({
        timeInterval,
        slotDuration,
        showUtilization,
      })
    }
    setIsTimeRangePopoverOpen(false)
  }

  // Calculate date range for notes based on calendar view
  const { from, until } = useMemo(() => {
    let fromDate: Date, untilDate: Date
    switch (calendarType) {
      case CalendarType.MONTH:
        fromDate = startOfMonth(selectedDate)
        untilDate = endOfMonth(selectedDate)
        break
      case CalendarType.WEEK:
        fromDate = addWeeks(selectedDate, -1)
        untilDate = addWeeks(selectedDate, 1)
        break
      case CalendarType.DAY:
        fromDate = selectedDate
        untilDate = selectedDate
        break
      default:
        fromDate = selectedDate
        untilDate = selectedDate
    }
    return {
      from: format(fromDate, 'yyyy-MM-dd'),
      until: format(untilDate, 'yyyy-MM-dd'),
    }
  }, [selectedDate.toISOString(), calendarType])

  // Use the daily notes hook
  const { notes, create, update, remove } = useDailyNotes(from, until)

  // Compute the current note for the selected date
  const currentNote = useMemo(() => {
    return notes.find((n) => isSameDay(new Date(n.date), selectedDate))
  }, [selectedDate.toISOString(), notes])

  const changeDateHandler = (date: Date | undefined) => {
    if (date) changeDate(date)
  }

  const handleCreateNote = () => {
    setShowNoteModal(true)
  }

  const handleSaveNote = async (note: CreateDailyNoteDto | UpdateDailyNoteDto, id?: string) => {
    if (id) {
      update(id, note as UpdateDailyNoteDto, () => setShowNoteModal(false))
    } else {
      create(note as CreateDailyNoteDto, () => setShowNoteModal(false))
    }
  }

  const handleDeleteNote = async (id: string) => {
    remove(id, () => setShowNoteModal(false))
  }

  const handleViewChange = (value: string) => {
    setCalendarType(value as CalendarType)
  }

  const handlePrevious = () => {
    let newDate: Date
    switch (calendarType) {
      case CalendarType.DAY:
        newDate = addDays(selectedDate, -1)
        break
      case CalendarType.WEEK:
        newDate = addWeeks(selectedDate, -1)
        break
      case CalendarType.MONTH:
        newDate = addMonths(selectedDate, -1)
        break
      default:
        return
    }
    changeDate(newDate)
  }

  const handleNext = () => {
    let newDate: Date
    switch (calendarType) {
      case CalendarType.DAY:
        newDate = addDays(selectedDate, 1)
        break
      case CalendarType.WEEK:
        newDate = addWeeks(selectedDate, 1)
        break
      case CalendarType.MONTH:
        newDate = addMonths(selectedDate, 1)
        break
      default:
        return
    }
    changeDate(newDate)
  }

  const handleToday = () => {
    changeDate(new Date())
  }

  const handleRefresh = () => {
    setDate(selectedDate)
  }

  const customDateDisplay = useMemo(() => {
    switch (calendarType) {
      case CalendarType.MONTH:
        return format(selectedDate, 'MMMM yyyy', { locale: de }) // e.g., "Oktober 2025"
      case CalendarType.WEEK:
        const week = getWeek(selectedDate, { locale: de, weekStartsOn: 1 })
        const year = format(selectedDate, 'yyyy')
        return `${week}KW ${year}` // e.g., "44KW 2025"
      case CalendarType.DAY:
        return format(selectedDate, 'cccc, d. MMMM yyyy', { locale: de }) // e.g., "17. Oktober 2025"
      default:
        return undefined
    }
  }, [calendarType, selectedDate])

  return (
    <>
      <div className="mb-4 grid gap-2 md:grid-cols-3 lg:gap-4">
        {/* Left section: View selector and Today button */}
        <div className="flex items-center gap-2">
          <Select value={calendarType} onValueChange={handleViewChange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CalendarType.MONTH}>Monat</SelectItem>
              {tenant?.type === TenantType.SERVICE && (
                <SelectItem value={CalendarType.WEEK}>Woche</SelectItem>
              )}
              <SelectItem value={CalendarType.DAY}>Tag</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleToday} variant="outline" className="whitespace-nowrap">
            Heute
          </Button>
        </div>

        {/* Center section: Date navigation */}
        <div className="flex items-center justify-center gap-2">
          <Button
            onClick={handlePrevious}
            variant="outline"
            size="icon"
            icon={<ChevronLeft className="h-4 w-4" />}
          />
          <DatePicker
            date={selectedDate}
            onDateChange={changeDateHandler}
            customDisplay={customDateDisplay}
            className="flex !w-full !max-w-full justify-center sm:!max-w-[18rem] md:!w-[18rem]"
          />
          <Button
            onClick={handleNext}
            variant="outline"
            size="icon"
            icon={<ChevronRight className="h-4 w-4" />}
          />
        </div>

        {/* Right section: Actions */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={handleCreateNote}
            variant="outline"
            icon={<NotebookPen className="h-4 w-4" />}
            title="Notiz für heute erstellen"
          />
          <Button
            onClick={() => setShowNoteView(!showNoteView)}
            variant={showNoteView ? 'default' : 'outline'}
            className={cn({
              'border-primary': currentNote,
            })}
            icon={<FileText className="h-4 w-4" />}
            title="Notizen anzeigen/verbergen"
          />
          <Popover open={isTimeRangePopoverOpen} onOpenChange={setIsTimeRangePopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" title="Zeitbereich konfigurieren">
                <CogIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium leading-none">Zeitbereich konfigurieren</h4>
                  <p className="text-muted-foreground text-sm">
                    Legen Sie den sichtbaren Zeitbereich für den Kalender fest.
                    {tenant?.timezone && (
                      <span className="mt-1 block text-xs">
                        Zeiten werden in der Zeitzone {tenant.timezone} interpretiert.
                      </span>
                    )}
                  </p>
                </div>
                <div className="grid gap-2">
                  <div className="grid grid-cols-3 items-center gap-4">
                    <Label htmlFor="minTime">Startzeit</Label>
                    <Input
                      id="minTime"
                      type="time"
                      value={minTime}
                      onChange={(e) => setMinTime(e.target.value)}
                      className="col-span-2 h-8"
                    />
                  </div>
                  <div className="grid grid-cols-3 items-center gap-4">
                    <Label htmlFor="maxTime">Endzeit</Label>
                    <Input
                      id="maxTime"
                      type="time"
                      value={maxTime}
                      onChange={(e) => setMaxTime(e.target.value)}
                      className="col-span-2 h-8"
                    />
                  </div>
                  <div className="grid grid-cols-3 items-center gap-4">
                    <Label htmlFor="timeInterval">Intervall</Label>
                    <Select
                      value={String(timeInterval)}
                      onValueChange={(v) => setTimeInterval(Number(v))}
                    >
                      <SelectTrigger className="col-span-2 h-8 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[15, 30, 60].map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {n} min
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-3 items-center gap-4">
                    <Label htmlFor="slotDuration">Slot-Dauer</Label>
                    <Select
                      value={String(slotDuration)}
                      onValueChange={(v) => setSlotDuration(Number(v))}
                    >
                      <SelectTrigger className="col-span-2 h-8 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[15, 30, 60].map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {n} min
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {tenant?.type === TenantType.FOOD && (
                    <div className="grid grid-cols-3 items-center gap-4">
                      <Label htmlFor="showUtilization">Auslastungen anzeigen</Label>
                      <Switch
                        id="showUtilization"
                        checked={showUtilization}
                        onCheckedChange={(checked) => setShowUtilization(checked)}
                      />
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsTimeRangePopoverOpen(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button size="sm" onClick={handleSave}>
                    Speichern
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <Button onClick={handleRefresh} icon={<RefreshCw className="h-4 w-4" />} />
          <Button onClick={() => setFullscreen(!fullscreen)}>
            {fullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
          </Button>
        </div>

        <DailyNoteModal
          open={showNoteModal}
          onOpenChange={setShowNoteModal}
          date={selectedDate}
          onSave={handleSaveNote}
          onDelete={handleDeleteNote}
        />
      </div>
      {showNoteView && (
        <div className="bg-muted mt-4 rounded-t border-x border-t p-4">
          <h3 className="mb-2 text-lg font-semibold">
            Notiz{calendarType === CalendarType.MONTH && notes.length > 1 ? 'en' : ''}
          </h3>
          {notes.length === 0 ? (
            <div className="text-muted-foreground">Keine Notizen im gewählten Zeitraum.</div>
          ) : (
            <ul className="space-y-2">
              {notes.map((note) => (
                <li key={note.id} className="bg-background rounded border p-3 shadow-sm">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="font-medium">
                      {calendarType === CalendarType.MONTH &&
                        format(new Date(note.date), 'd. MMMM yyyy', { locale: de })}
                    </span>
                  </div>
                  <div className="whitespace-pre-line text-sm">{note.note}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  )
}

export default Toolbar
